import React, { useState, useRef } from 'react';
import { Box, Typography, Popper, ClickAwayListener } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BlinqxLogo } from '@/icons';
import { LanguageMenu, ClickableIcon } from '@/components/common';
import { Language } from '@/types';
import { getSpacing } from '@/styles/theme';
import { AuthSection } from '../../AuthSection';

const Header: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageButtonRef = useRef<HTMLDivElement>(null);

  const handleLanguageToggle = (): void => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const handleClickAway = (): void => {
    setIsLanguageMenuOpen(false);
  };

  const handleLogoClick = (): void => {
    navigate('/');
  };

  const getCurrentLanguageDisplay = (): string => {
    return i18n.language === Language.EN ? 'EN' : 'NL';
  };

  return (
    <Box sx={{ width: '100%', bgcolor: 'surface.neutralDark' }}>
      <Box
        sx={{
          height: 64,
          px: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <ClickableIcon onClick={handleLogoClick}>
          <BlinqxLogo />
        </ClickableIcon>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: getSpacing.sm,
          }}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ position: 'relative' }}>
              <Box
                ref={languageButtonRef}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: getSpacing.sm,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={handleLanguageToggle}
              >
                <LanguageIcon sx={{ fontSize: 24, color: 'common.white' }} />
                <Typography
                  sx={{
                    color: 'common.white',
                    fontSize: 14,
                    fontWeight: (theme) => theme.customFontWeights.semibold,
                    lineHeight: '20px',
                    userSelect: 'none',
                  }}
                >
                  {getCurrentLanguageDisplay()}
                </Typography>
                <ArrowDropDownIcon
                  sx={{
                    fontSize: 24,
                    color: 'surface.navItemInactive',
                    transform: isLanguageMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Box>

              <Popper
                open={isLanguageMenuOpen}
                anchorEl={languageButtonRef.current}
                placement='bottom-end'
                sx={{ zIndex: 1300 }}
              >
                <LanguageMenu onLanguageChange={handleClickAway} />
              </Popper>
            </Box>
          </ClickAwayListener>

          <AuthSection />
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(Header);
